import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {BaseService} from "@6grain/masofa-monolith-api-client/dictionaries/api.base.service";
import {InputComponent} from "@components/input";
import {CheckboxComponent} from "@components/checkbox";
import {RadioBoxComponent} from "@components/radio-box";
import {MsfSelect} from "@components/select/select";
import {MsfTreeSelect} from "@components/tree-select/tree-select";
import {DatePickerComponent} from "@components/date-picker";
import {NotReadyApiService} from "@app/api/not-ready-api-service";
import {TextareaComponent} from "@components/textarea";
import {FieldService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {InputGroup} from 'primeng/inputgroup';
import {Button} from 'primeng/button';
import {InputText} from 'primeng/inputtext';
import {InputGroupAddon} from 'primeng/inputgroupaddon';
import {DocumentFileUploadComponent} from '@components/document-file-upload/document-file-upload.component';
import {MsfSelectPaginated} from '@components/select/paginated/select-paginated';
import {MsfMultiSelect} from '@components/multi-select/multi-select';
import {FilterOperator} from '@app/utils/filter-operator';
import {Subject} from 'rxjs';

export interface IMsfFormBuilder {
  selectOptionValue?: string;
  selectOptionLabel?: string;
  placeholder?: string;
  name: string;
  type: 'input' |
    'select' |
    'select-paginate' |
    'multi-select' |
    'tree-select' |
    'checkbox' |
    'radio-box' |
    'textarea' |
    'input-number' |
    'input-double' |
    'date' |
    'input-group' |
    'inline-input' |
    'fileUpload';
  title?: string;
  selectService?: BaseService | NotReadyApiService | FieldService | any;
  selectServiceMethod?: string;
  selectOptions?: any[] | any;
  getSelectOptions?: () => any[];
  filter?: any;
  metaField?: string;
  required?: boolean;
  hide?: () => boolean;
  disabled?: boolean | (() => boolean);
  dependsOn?: string;
  dependsOnFilterKey?: string;
  conditionalSelectService?:
    | { when?: (parentValue: any) => boolean; service: any; method: string }
    | { when?: (parentValue: any) => boolean; service: any; method: string }[];
  _reloadToken?: number;
  options?: {
    clearable?: boolean;
      date?: {
        selectionMode?: 'single' | 'multiple' | 'range',
        showTime?: boolean,
        showSeconds?: boolean,
        dateOnly?: boolean,
        yearOnly?: boolean,
        monthOnly?: boolean,
        dayOnly?: boolean,
        monthAndDayOnly?: boolean,
        minDate?: Date | null,
        maxDate?: Date | null,
        maxDateFromField?: string,
        allowOnlySelectedDate?: boolean,
        selectableDates?: () => (String | Date)[];
        onYearChange?: (year?:number) => void;
        onMonthChange?: (month?:number) => void;
      },
    select?: {
      optionLabel?: string,
      optionSubtitle?: string,
      optionSubtitleJoiner?: string,
      optionValue?: string,
      translateBy?: string | 'none' | 'locale' | null,
      isUser?: boolean,
      loading?: boolean | (() => boolean),
      //@deprecated use filter instead
      searchBy?: string,
      searchKeywords?: string[],
      filter?: Array<{
        filterField: string,
        filterOperator: FilterOperator
        filterValue:string | number
      }>
    }
    treeSelect?: {
      optionLabel?: string,
      optionValue?: string,
      translateBy?: string | 'none' | null,
      treeBy?: string,
      isUser?: boolean
    },
    inputGroup?: {
      iconLeft?: string;
      iconRight?: string;
      actionLeft?: () => void;
      actionRight?: () => void;
    },
    radioBox?: {
      allLabel?: string;
      yesLabel?: string;
      noLabel?: string;
      showAll?: boolean;
      showYes?: boolean;
      showNo?: boolean;
      direction?: 'horizontal' | 'vertical';
    },
    col?: string;
    inlineInput?: IMsfFormBuilder[]
  }
}


@Component({
  selector: 'msf-form-builder',
  imports: [
    FormsModule,
    CommonModule,
    TranslateModule,
    InputComponent,
    CheckboxComponent,
    RadioBoxComponent,
    MsfSelect,
    MsfTreeSelect,
    DatePickerComponent,
    TextareaComponent,
    FormsModule,
    InputGroup,
    Button,
    InputText,
    InputGroupAddon,
    DocumentFileUploadComponent,
    MsfSelectPaginated,
    MsfMultiSelect,
  ],
  templateUrl: './form-builder.html',
  styleUrl: './form-builder.scss',
  standalone: true,
})
export class FormBuilder implements OnInit, OnChanges, OnDestroy {
  flexRowSize = 'col-12';
  private destroy$ = new Subject<void>();

  @Input({required: true}) fields: IMsfFormBuilder[] | undefined = [];
  @Input({required: true}) model: any;
  @Input() metaData: any;
  @Input() flexDirection: 'row' | 'column' = 'column';

  @Output() modelChange = new EventEmitter<any>();

  isFieldDisabled(field: IMsfFormBuilder): boolean {
    if (field.disabled == null) {
      return false;
    }
    return typeof field.disabled === 'function' ? field.disabled() : field.disabled;
  }

  getPlaceholder(field: IMsfFormBuilder): string {
    if (field.placeholder) {
      return field.placeholder;
    }
    if (field.type === 'select' || field.type === 'date') {
      return `Select ${field.title?.toLowerCase()}`
    }
    return `Enter ${field.title?.toLowerCase()}`
  }
  constructor(private translateService: TranslateService) {}

  ngOnChanges(changes: any) {
    if (changes['metaData']) {
      this.optionsCache.clear();
    }
    // When a new model reference is provided, rewire dependencies
    if (changes['model'] && changes['model'].previousValue !== changes['model'].currentValue) {
      this._fieldWatchers.clear();
      this.setupDependencies();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  get flexDirectionOptions() {
    if (this.flexDirection === 'row') {
      return ' flex-row flex-wrap justify-content-between'
    } else {
      return 'flex-column gap-3'
    }

  }

  ngOnInit(): void {
    if (this.fields) {
      const fieldCount = this.fields.length;

      if (fieldCount <= 6) {
        this.flexRowSize = 'col-12';
      } else if (fieldCount <= 10) {
        this.flexRowSize = 'col-12 md:col-6';
      } else if (fieldCount <= 15) {
        this.flexRowSize = 'col-12 md:col-4';
      } else if (fieldCount <= 20) {
        this.flexRowSize = 'col-12 md:col-3';
      } else {
        this.flexRowSize = 'col-12 md:col-2';
      }
    }

    this.fields?.forEach((field) => {
      if (field.type === 'input-number' || field.type === 'input-double') {
        this.model[field.name] = this.formatToTwoDecimals(this.model[field.name]);
      }
    });

    this.setupDependencies();
  }

  /**
   * Generically wires up fields that have a `dependsOn` property.
   * When the parent field changes:
   *   1. Update selectService / selectServiceMethod on the dependent field
   *   2. Update the filter with the parent's value (using dependsOnParamKey)
   *   3. Bump _reloadToken so the [reload] binding fires resetAndSearch() in msf-select-paginated
   */
  private setupDependencies(): void {
    if (!this.fields) return;

    this.setupSelectDependencies();
    this.setupDateDependency();
  }

  /**
   * Select dependency wiring (existing behaviour for conditionalSelectService)
   */
  private setupSelectDependencies(): void {
    if (!this.fields) return;

    this.fields.forEach(field => {
      if (!field.dependsOn || !field.conditionalSelectService) return;

      const applyDependency = (parentValue: any, isInit = false) => {
        const resolved = this.resolveConditionalService(field.conditionalSelectService!, parentValue);
        if (!resolved) return;

        const { service, method } = resolved;

        // 1. Switch service + method on the dependent field
        field.selectService = service;
        field.selectServiceMethod = method;

        // 2. Build filter from parent value
        if (field.dependsOnFilterKey && parentValue != null) {
          const filterValue = parentValue?.id ?? parentValue;
          field.options = {
            ...(field.options ?? {}),
            select: {
              ...(field.options?.select ?? {}),
              filter: [
                {
                  filterField: field.dependsOnFilterKey,
                  filterOperator: FilterOperator.Equals,
                  filterValue
                }
              ]
            }
          };
        } else {
          if (field.options?.select?.filter) {
            field.options.select.filter = [];
          }
        }

        // 3. On init (edit mode): do NOT reset the child value, just reload the options.
        //    On user change: reset child value so stale selection is cleared.
        if (!isInit) {
          if (this.model && field.name in this.model) {
            this.model[field.name] = null;
            // this.modelChange.emit(this.model);
          }
        }

        field._reloadToken = Date.now();
      };

      // Watch future changes
      this.watchModelField(field.dependsOn, (parentValue: any) => {
        applyDependency(parentValue, false);
      });

      // If parent already has a value on init (edit mode), apply immediately
      const existingParentValue = this.model?.[field.dependsOn];
      if (existingParentValue != null) {
        applyDependency(existingParentValue, true);
      }
    });
  }

  /**
   * Ensures dependent date fields respect a maximum derived from another date field.
   */
  private setupDateDependency(): void {
    if (!this.fields || !this.model) return;

    const dateDependencies = this.fields.filter(
      field => field.options?.date?.maxDateFromField
    );

    const ensureOptions = (field: IMsfFormBuilder) => {
      field.options = field.options || {};
      field.options.date = field.options.date || {};
    };

    dateDependencies.forEach(childField => {
      const parentFieldName = childField.options!.date!.maxDateFromField!;
      const parentField = this.fields?.find(f => f.name === parentFieldName);
      if (!parentField) return;

      const applyDateConstraint = (parentValue: any, isInit = false) => {
        ensureOptions(childField);

        const parentDate = parentValue ? new Date(parentValue) : null;
        childField.options!.date!.maxDate = parentDate;

        if (parentDate && this.model[childField.name]) {
          const childDate = new Date(this.model[childField.name]);
          if (childDate > parentDate) {
            this.model[childField.name] = null;
            if (!isInit) {
              this.modelChange.emit(this.model);
            }
          }
        }
      };

      this.watchModelField(parentField.name, (value: any) => applyDateConstraint(value, false));

      if (this.model[parentField.name]) {
        applyDateConstraint(this.model[parentField.name], true);
      }
    });
  }

  /**
   * Resolves the correct service/method entry from conditionalSelectService.
   * - If it's a single object: use it directly (ignore `when`).
   * - If it's an array: return the first entry whose `when(parentValue)` returns true,
   *   or the first entry without a `when` (fallback).
   */
  private resolveConditionalService(
    config: IMsfFormBuilder['conditionalSelectService'],
    parentValue: any
  ): { service: any; method: string } | null {
    if (!config) return null;

    // Single entry (no array)
    if (!Array.isArray(config)) {
      return config;
    }

    // Array: find first matching condition
    const match = config.find(entry => entry.when ? entry.when(parentValue) : false);
    if (match) return match;

    // Fallback: first entry without a `when`
    const fallback = config.find(entry => !entry.when);
    return fallback ?? null;
  }

  /**
   * Watches a property on model via a JS getter/setter proxy.
   * Safe to call multiple times — only installs once per field name.
   */
  private _fieldWatchers = new Map<string, Array<(value: any) => void>>();

  /**
   * Shared watcher registry per model instance, so multiple FormBuilder instances
   * can observe the same field without clobbering each other's getters/setters.
   */
  private static modelFieldWatchers = new WeakMap<any, Map<string, { value: any; callbacks: Array<(value: any) => void> }>>();

  private watchModelField(fieldName: string, callback: (value: any) => void): void {
    if (!this.model) return;

    // Track callbacks per instance to avoid duplicate registrations inside one FormBuilder
    const localCallbacks = this._fieldWatchers.get(fieldName) ?? [];
    if (!localCallbacks.includes(callback)) {
      localCallbacks.push(callback);
      this._fieldWatchers.set(fieldName, localCallbacks);
    }

    // Shared registry per model instance and field
    let modelWatchers = FormBuilder.modelFieldWatchers.get(this.model);
    if (!modelWatchers) {
      modelWatchers = new Map();
      FormBuilder.modelFieldWatchers.set(this.model, modelWatchers);
    }

    const existingEntry = modelWatchers.get(fieldName);
    if (existingEntry) {
      if (!existingEntry.callbacks.includes(callback)) {
        existingEntry.callbacks.push(callback);
      }
      return; // already wrapped for this model/field
    }

    const entry = { value: this.model[fieldName], callbacks: [callback] };
    modelWatchers.set(fieldName, entry);

    Object.defineProperty(this.model, fieldName, {
      configurable: true,
      enumerable: true,
      get: () => entry.value,
      set: (newValue: any) => {
        const previousValue = entry.value;
        entry.value = newValue;
        if (newValue !== previousValue) {
          entry.callbacks.forEach(cb => cb(newValue));
        }
      }
    });
  }


  getCol(field: IMsfFormBuilder, fieldsT:IMsfFormBuilder[], $index:number): string {
    if (field.options?.col) {
      return field.options.col;
    }
    return (fieldsT.length % 2 !== 0 && $index === fieldsT.length - 1 ? 'col-12' : this.flexRowSize);
  }

  /**
   * Получает опции для select поля из метаданных
   */
  private optionsCache = new Map<string, any[]>();

  getSelectOptionsFromMeta(field: IMsfFormBuilder): any[] {
    const cacheKey = `${field.name}_${field.metaField}`;

    if (this.optionsCache.has(cacheKey)) {
      return this.optionsCache.get(cacheKey)!;
    }

    if (!field.metaField || !this.metaData || !this.metaData[field.metaField]) {
      return [];
    }

    return this.metaData[field.metaField]
  }


  onFileSelected(fileInfo: any, fieldName: string): void {
    this.model[fieldName] = fileInfo.fileStorageId;
    this.modelChange.emit(this.model);
  }

  onFileRemoved(fieldName: string): void {
    this.model[fieldName] = null;
    this.modelChange.emit(this.model);
  }

  limitDecimals(event: Event, fieldName: string) {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    if (value.includes('.')) {
      const [int, dec] = value.split('.');
      if (dec.length > 2) {
        value = `${int}.${dec.slice(0, 2)}`;
        input.value = value;
        this.model[fieldName] = value;
        this.modelChange.emit(this.model);
      }
    }
  }

  formatToTwoDecimals(value: any): any {
    if (typeof value === 'number') {
      return parseFloat(value.toFixed(2));
    }
    // if (typeof value === 'string' && value.includes('.')) {
    //   const [int, dec] = value.split('.');
    //   return `${int}.${dec.slice(0, 2)}`;
    // }
    return value;
  }

  getClear(field: any): boolean {
    if (field.options?.clearable === null || field.options?.clearable === undefined) {
      return true;
    }
    return field.options.clearable;
  }

  getDatePickerSelectableDates(field: IMsfFormBuilder): (String | Date)[]   {
    if (field.options?.date?.selectableDates) {
      return field.options.date.selectableDates();
    }
    return [];
  }

  onDatePickerYearChanger(field: IMsfFormBuilder, year:number): void   {
    if (field.options?.date?.onYearChange) {
      field.options.date.onYearChange(year);
    }
  }
  onDatePickerMonthChanger(field: IMsfFormBuilder, month:number): void   {
    if (field.options?.date?.onMonthChange) {
      field.options.date.onMonthChange(month);
    }
  }
}
